import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import "cropperjs/dist/cropper.css"

// Wraps any <input type="file" accept="image/*">. When the user picks
// an image, opens a modal with Cropper.js: crop, zoom, rotate, aspect
// ratio. Confirm replaces the input's File with the edited blob via
// DataTransfer, then re-dispatches `change` so other controllers
// (e.g. image-preview) refresh against the edited file. Cancel clears
// the input so the user has to pick again.
//
// Non-image files (videos in mixed image+video uploaders) skip the
// editor — we just let the original change event flow through.
export default class extends Controller {
  static SAFE_FRAME_AREA = 0.88

  static values = {
    // Optional default aspect ratio: "free" | "1" | "16:9" | "4:3" | "3:4".
    // Free is the default; per-form values let cars/features lock to
    // their canonical ratio if we want to enforce one later.
    aspect: { type: String, default: "free" },
    // Optional URL to a transparent silhouette SVG (e.g. /vite-dev/assets/
    // car-silhouette.svg). When set, the SVG is overlaid on top of the
    // crop area as a framing guide — the user pans/zooms the image
    // underneath so it lines up with the outline. Bruno's 2026-05-18 ask
    // for the car/model cover flow.
    silhouetteUrl: { type: String, default: "" }
  }

  connect() {
    this._onChange = this._onChange.bind(this)
    this._onResize = this._onResize.bind(this)
    this.element.addEventListener("change", this._onChange)
  }

  disconnect() {
    this.element.removeEventListener("change", this._onChange)
    window.removeEventListener("resize", this._onResize)
    this._cleanup()
  }

  _onChange(event) {
    // Skip the synthetic change we dispatch after a successful edit —
    // otherwise we'd re-open the modal on top of the just-edited file.
    if (this._skipNext) {
      this._skipNext = false
      return
    }

    const file = this.element.files && this.element.files[0]
    if (!file) return
    if (!file.type || !file.type.startsWith("image/")) return

    this._open(file)
  }

  _open(originalFile) {
    this._originalFile = originalFile

    const reader = new FileReader()
    reader.onload = (e) => this._mount(e.target.result)
    reader.readAsDataURL(originalFile)
  }

  _mount(dataUrl) {
    const overlay = document.createElement("div")
    overlay.className = "image-editor-overlay"
    overlay.innerHTML = `
      <div class="image-editor-modal">
        <div class="image-editor-modal__header">
          <h3>${this._t("title")}</h3>
          <button type="button" class="btn btn-sm btn-ghost btn-circle" data-ie-action="cancel" aria-label="${this._t("cancel")}">✕</button>
        </div>
        <div class="image-editor-modal__stage">
          <img alt="" />
        </div>
        <div class="image-editor-modal__toolbar">
          <div class="join">
            <button type="button" class="btn btn-sm join-item" data-ie-action="rotate-left" title="${this._t("rotateLeft")}">⟲</button>
            <button type="button" class="btn btn-sm join-item" data-ie-action="rotate-right" title="${this._t("rotateRight")}">⟳</button>
            <button type="button" class="btn btn-sm join-item" data-ie-action="zoom-in" title="${this._t("zoomIn")}">+</button>
            <button type="button" class="btn btn-sm join-item" data-ie-action="zoom-out" title="${this._t("zoomOut")}">−</button>
            <button type="button" class="btn btn-sm join-item" data-ie-action="reset" title="${this._t("reset")}">↺</button>
          </div>
          <select class="select select-sm select-bordered" data-ie-aspect>
            <option value="NaN">${this._t("aspect.free")}</option>
            <option value="1">${this._t("aspect.square")}</option>
            <option value="${16 / 9}">${this._t("aspect.video")}</option>
            <option value="${5 / 3}">${this._t("aspect.card")}</option>
            <option value="${4 / 3}">${this._t("aspect.classic")}</option>
            <option value="${3 / 4}">${this._t("aspect.portrait")}</option>
          </select>
          <div class="image-editor-modal__spacer"></div>
          <button type="button" class="btn btn-sm btn-ghost" data-ie-action="cancel">${this._t("cancel")}</button>
          <button type="button" class="btn btn-sm btn-primary" data-ie-action="confirm">${this._t("confirm")}</button>
        </div>
      </div>
    `
    document.body.appendChild(overlay)
    this._overlay = overlay

    const img = overlay.querySelector("img")
    img.onload = () => this._initCropper(img)
    img.src = dataUrl

    overlay.querySelectorAll("[data-ie-action]").forEach((el) => {
      el.addEventListener("click", () => this._dispatch(el.dataset.ieAction))
    })
    const select = overlay.querySelector("[data-ie-aspect]")
    select.addEventListener("change", (e) => {
      this._setAspectRatio(parseFloat(e.target.value))
    })

    // Preselect aspect from the values attribute if set.
    const initial = this._aspectToRatio(this.aspectValue)
    this._currentAspectRatio = initial
    if (!Number.isNaN(initial)) {
      const opt = Array.from(select.options).find((o) => parseFloat(o.value) === initial)
      if (opt) select.value = opt.value
    }

    window.addEventListener("resize", this._onResize)
  }

  _initCropper(img) {
    if (this._cropper) this._cropper.destroy()
    // When a silhouette overlay is supplied the crop box acts as a fixed
    // framing reference — the user pans/zooms the image *under* it,
    // matching Bruno's "mascara em cima" mental model. Drop the resize/
    // move handles for that mode and switch dragMode so drag-on-stage
    // pans the photo instead of redrawing the crop area. Without a
    // silhouette the editor behaves like a normal Cropper (free crop).
    const hasSilhouette = Boolean(this.silhouetteUrlValue)
    this._cropper = new Cropper(img, {
      viewMode: 1,
      autoCropArea: 1,
      responsive: true,
      background: false,
      movable: true,
      zoomable: true,
      rotatable: true,
      scalable: false,
      dragMode: hasSilhouette ? "move" : "crop",
      cropBoxMovable: !hasSilhouette,
      cropBoxResizable: !hasSilhouette,
      toggleDragModeOnDblclick: false,
      aspectRatio: this._currentAspectRatio,
      ready: () => {
        this._fitSafeFrame()
        this._mountSilhouette()
      }
    })
  }

  _setAspectRatio(ratio) {
    if (!this._cropper) return

    this._currentAspectRatio = ratio
    this._cropper.setAspectRatio(ratio)
    window.requestAnimationFrame(() => {
      this._fitSafeFrame(ratio)
      this._mountSilhouette()
    })
  }

  _onResize() {
    if (!this._cropper) return

    window.requestAnimationFrame(() => {
      this._fitSafeFrame()
      this._mountSilhouette()
    })
  }

  _fitSafeFrame(ratio = this._currentAspectRatio) {
    if (!this._cropper || !this.silhouetteUrlValue) return

    const container = this._cropper.getContainerData()
    if (!container.width || !container.height) return

    const area = this.constructor.SAFE_FRAME_AREA
    let width = container.width * area
    let height = container.height * area

    if (!Number.isNaN(ratio) && ratio > 0) {
      height = width / ratio
      if (height > container.height * area) {
        height = container.height * area
        width = height * ratio
      }
    }

    this._cropper.setCropBoxData({
      left: (container.width - width) / 2,
      top: (container.height - height) / 2,
      width,
      height
    })
  }

  // Adds the silhouette overlay inside the Cropper crop-box once Cropper
  // has finished setting up. The crop box is locked in size/position when
  // the silhouette is shown (see _initCropper), so attaching it here
  // gives us a fixed-on-screen reference that scales with the locked
  // crop area. pointer-events: none lets the underlying canvas keep
  // receiving pan/zoom drags.
  _mountSilhouette() {
    if (!this.silhouetteUrlValue) return
    const cropBox = this._overlay && this._overlay.querySelector(".cropper-crop-box")
    if (!cropBox) return

    cropBox.querySelectorAll(".image-editor-silhouette").forEach((el) => el.remove())

    const guide = document.createElement("img")
    guide.src = this.silhouetteUrlValue
    guide.alt = ""
    guide.className = "image-editor-silhouette"
    guide.style.position = "absolute"
    guide.style.inset = "0"
    guide.style.width = "100%"
    guide.style.height = "100%"
    guide.style.objectFit = "contain"
    guide.style.pointerEvents = "none"
    // Lower than the default 0.45 because the Ford silhouette ships
    // with solid fills + its own opacity: .8 baked in — stacking two
    // alpha layers kept the photo fully hidden.
    guide.style.opacity = "0.35"
    guide.style.mixBlendMode = "multiply"
    guide.style.zIndex = "5"
    cropBox.appendChild(guide)
    this._silhouette = guide
  }

  _aspectToRatio(s) {
    if (!s || s === "free") return NaN
    if (s.includes(":")) {
      const [a, b] = s.split(":").map(Number)
      return a / b
    }
    return parseFloat(s)
  }

  _dispatch(action) {
    if (!this._cropper && action !== "cancel") return
    switch (action) {
      case "rotate-left": this._cropper.rotate(-90); break
      case "rotate-right": this._cropper.rotate(90); break
      case "zoom-in":  this._cropper.zoom(0.1); break
      case "zoom-out": this._cropper.zoom(-0.1); break
      case "reset":    this._cropper.reset(); break
      case "cancel":   this._cancel(); break
      case "confirm":  this._confirm(); break
    }
  }

  _cancel() {
    // Drop the file selection — user has to pick again. We don't want to
    // silently keep the un-edited file just because they hit Cancel.
    this.element.value = ""
    // Re-fire change so sibling preview controllers (image-preview,
    // cover-image-picker) see an empty files list and drop the preview
    // they already painted from the original file. _skipNext stops our
    // own handler from re-opening the editor on a no-op change.
    this._skipNext = true
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
    this._cleanup()
  }

  _confirm() {
    const canvas = this._cropper.getCroppedCanvas({
      maxWidth: 4096,
      maxHeight: 4096,
      imageSmoothingQuality: "high"
    })

    // Match the original mime when it's something the canvas can encode
    // (jpeg/png/webp); fall back to jpeg for anything else (e.g. heic).
    const mime = this._encodableMime(this._originalFile.type)
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this._cleanup()
          return
        }
        const file = new File([blob], this._originalFile.name, {
          type: mime,
          lastModified: Date.now()
        })
        const dt = new DataTransfer()
        dt.items.add(file)
        this.element.files = dt.files

        // Re-fire change so image-preview (and friends) reflect the
        // edited file. _skipNext stops our own handler from re-opening.
        this._skipNext = true
        this.element.dispatchEvent(new Event("change", { bubbles: true }))

        this._cleanup()
      },
      mime,
      0.9
    )
  }

  _encodableMime(mime) {
    if (mime === "image/png" || mime === "image/webp") return mime
    return "image/jpeg"
  }

	  _cleanup() {
	    window.removeEventListener("resize", this._onResize)
	    if (this._cropper) {
      this._cropper.destroy()
      this._cropper = null
    }
    if (this._overlay) {
      this._overlay.remove()
      this._overlay = null
    }
    this._originalFile = null
  }

  // Minimal in-controller i18n. We pull strings from
  // `window.imageEditorI18n` if the layout has set them; otherwise fall
  // back to pt-BR defaults so the editor is usable even before the
  // strings ship.
  _t(key) {
    const dict = (typeof window !== "undefined" && window.imageEditorI18n) || {}
    const path = key.split(".")
    let node = dict
    for (const k of path) {
      if (node && typeof node === "object" && k in node) node = node[k]
      else { node = null; break }
    }
    if (node) return node
    const defaults = {
      title: "Editar imagem",
      cancel: "Cancelar",
      confirm: "Aplicar",
      rotateLeft: "Girar para a esquerda",
      rotateRight: "Girar para a direita",
      zoomIn: "Aumentar",
      zoomOut: "Diminuir",
      reset: "Restaurar",
      aspect: {
        free: "Livre",
        square: "1:1 (Quadrado)",
        video: "16:9 (Vídeo)",
        card: "5:3 (Card do veículo)",
        classic: "4:3",
        portrait: "3:4 (Retrato)"
      }
    }
    let f = defaults
    for (const k of path) f = f && f[k]
    return f || key
  }
}
